import { z } from 'zod';
import { BlockSchema } from './blocks.js';

// ─────────────────────────────────────────────────────────────────
// Site-level email footer config
//
// Stored on siteSettings.emailFooter, and optionally overridden per email
// (see email-footer.ts).
//
// The footer is user-authored content: `blocks` holds whatever the user built
// in the footer editor, and the theme renders it through the same email block
// renderers as the body. The remaining fields are the *footer details*: they
// are no longer composed into fixed markup, they are the data behind the
// system variables ({{tagline}}, {{logo_url}}, {{socials}}, {{address}},
// {{legal_text}}) that footer content can reference — so a footer stays live
// when the details change.
//
// When `blocks` is unset the theme falls back to its own default footer, which
// is also what an older row (written before footers were authorable) gets.
// ─────────────────────────────────────────────────────────────────
export const EmailFooterConfigSchema = z
  .object({
    /** Footer content authored in the editor. Unset ⇒ inherit/theme default. */
    blocks: z.array(BlockSchema).optional(),
    /** One-line tagline under the site name. */
    tagline: z.string().max(200).optional(),
    /** Logo/signature image URL (uploads or external, e.g. an animated GIF). */
    logoUrl: z.string().url().optional(),
    logoAlt: z.string().max(120).optional(),
    socials: z
      .array(
        z.object({
          /** Display label, e.g. "X", "GitHub". */
          platform: z.string().min(1).max(40),
          url: z.string().url(),
        }),
      )
      .optional(),
    /** Physical/mailing address line, e.g. for legal senders. */
    address: z.string().max(300).optional(),
    /** Legal/company text, e.g. "© 2026 Acme Inc. All rights reserved." */
    legalText: z.string().max(500).optional(),
    /** Whether to render the unsubscribe link (required by law for bulk mail). */
    showUnsubscribe: z.boolean().default(true),
  })
  .strict();

export type EmailFooterConfig = z.infer<typeof EmailFooterConfigSchema>;
export type EmailFooterSocial = NonNullable<EmailFooterConfig['socials']>[number];
